// ============= Imports =============
import { AuthContextProps, tokenManager } from '@tw/tw-component-library';
import { ApiResponse } from '../types';

// ============= Types =============
interface billsType {
  selectedClientUno: string;
  selectedMatterUno: string;
  getAccessToken: AuthContextProps['getAccessToken']; // Added getAccessToken
}

// ============= Constants =============
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';
const PROD_BASE_URL = import.meta.env.VITE_REQUEST_URL;
const API_ENDPOINT = '/api/bills';
const SIDETRADE_SCOPE =
  'api://b922470e-04e2-4c04-84fc-e5ec591459ad/SidetradeFrontEndProd';
const HEADERS = {
  'x-functions-key': 'Test',
  'Content-Type': 'application/json',
};

// Development constants
const DEV_CLIENT_UNO = 'ABB60';
const DEV_MATTER_UNO = 'U30';

// ============= Utility Functions =============
/**
 * Creates headers for API bills including authentication
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

/**
 * Constructs the API URL based on environment and parameters
 * @param clientMatter The client matter string
 * @returns The constructed URL
 */
const constructApiUrl = (clientMatter: string): string => {
  if (IS_DEVELOPMENT) {
    // In development, use relative URL for proxy
    return `${API_ENDPOINT}?clientMatter=${encodeURIComponent(clientMatter)}`;
  } else {
    // In production, use full URL
    return `${PROD_BASE_URL}${API_ENDPOINT}?clientMatter=${encodeURIComponent(
      clientMatter
    )}`;
  }
};

// ============= Main Function =============
/**
 * Fetches bills from the API based on client and matter
 * @param selectedClientUno Client UNO
 * @param selectedMatterUno Matter UNO
 * @param getAccessToken Function to get access token
 * @returns Promise with API response containing bill data or error
 */
export const getBills = async ({
  selectedClientUno,
  selectedMatterUno,
  getAccessToken,
}: billsType): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `🔄 Fetching API data in ${
        IS_DEVELOPMENT ? 'development' : 'production'
      } mode...`
    );

    // Token acquisition with required scopes
    const token = await getAccessToken([SIDETRADE_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire Graph API token');
    }
    tokenManager.logTokenInfo(token, 'Graph API');

    // Determine which client/matter values to use
    const clientUno = IS_DEVELOPMENT ? DEV_CLIENT_UNO : selectedClientUno;
    const matterUno = IS_DEVELOPMENT ? DEV_MATTER_UNO : selectedMatterUno;

    // Construct the client matter string
    const clientMatter = `${clientUno}.${matterUno}`;

    // Get the appropriate URL based on environment
    const url = constructApiUrl(clientMatter);

    console.log('🌐 Making request to:', url);
    console.log('Using client:', clientUno);
    console.log('Using matter:', matterUno);

    // Configure fetch options based on environment
    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: createHeaders(token),
      // Include credentials only in development for proxy
      ...(IS_DEVELOPMENT && { credentials: 'include' }),
    };

    const response = await fetch(url, fetchOptions);

    // Response handling
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response not OK:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ API data fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error fetching API data:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};

// ============= Development Helper =============
/**
 * Development-only helper to test the API
 */
if (IS_DEVELOPMENT) {
  console.log('🔧 Running in development mode');
  console.log(`Using fixed client: ${DEV_CLIENT_UNO}`);
  console.log(`Using fixed matter: ${DEV_MATTER_UNO}`);
}
